<template>
  <div class="workoutZone">
    <div
      class="workoutZone__head"
      :class="{
        'workoutZone__head--actions': zone.id === 'actions',
        'workoutZone__head--hasFootTip': zone.id === 'music',
      }"
    >
      <span class="workoutZone__badge" aria-hidden="true">{{ zone.badge }}</span>
      <div class="workoutZone__headText">
        <h4 class="workoutZone__title">{{ zone.title }}</h4>
        <p class="workoutZone__desc">{{ zone.description }}</p>
      </div>
      <div v-if="zone.id === 'actions'" class="workoutZone__headCourseStats">
        <div class="workoutZone__headCourseStat">
          <span class="workoutZone__headCourseTotalLabel">{{ fieldLabels.totalDurationSeconds }}</span>
          <span class="workoutZone__headCourseTotalValue link-font-mono">{{ totalDurationLabel }}</span>
        </div>
      </div>
      <span
        class="workoutZone__count link-font-mono"
        :class="{ 'workoutZone__count--full': atCapacity }"
        :title="`本区最多 ${maxBindings} 条`"
      >
        {{ rows.length }} / {{ maxBindings }}
      </span>
      <div v-if="zone.id === 'actions'" class="workoutZone__headLegend">
        <workout-action-binding-legend :field-labels="fieldLabels" :binding-tips="bindingTips" />
      </div>
      <p v-if="zone.id === 'music'" class="workoutZone__headFootTip">{{ bindingTips.collectionLoop }}</p>
    </div>

    <div class="workoutZone__body">
      <div v-if="!rows.length" class="workoutZone__empty">
        <button type="button" class="workoutZone__pickBtn" @click="$emit('pick')">
          <i class="el-icon-plus workoutZone__pickIcon" aria-hidden="true" />
          <span class="workoutZone__pickLabel">{{ zone.pickLabel }}</span>
          <span class="workoutZone__pickHint">{{ zone.pickHint }}</span>
        </button>
      </div>

      <template v-else>
        <ul ref="sortableList" class="workoutZone__list">
          <li
            v-for="(row, index) in rows"
            :key="row.uid || (zone.id + '-' + row.resource_id + '-' + index)"
            class="workoutZone__item"
          >
            <div class="workoutZone__main">
              <div class="workoutZone__nameRow workoutZone__dragHandle" title="拖动排序；也可点序号改数字">
                <!-- 可输入序号：失焦/回车按目标位插入；mousedown.stop 避免 Sortable 抢焦点 -->
                <input
                  class="workoutZone__order link-font-mono"
                  type="text"
                  inputmode="numeric"
                  :value="index + 1"
                  :aria-label="`${fieldLabels.order} ${index + 1}`"
                  :title="`${fieldLabels.order} ${index + 1}（可输入）`"
                  @mousedown.stop
                  @click.stop
                  @keydown.enter.prevent="commitOrderInput(index, $event)"
                  @blur="commitOrderInput(index, $event)"
                />
                <span class="workoutZone__drag" aria-hidden="true">
                  <i class="el-icon-rank" />
                </span>
                <div class="workoutZone__infoBlock">
                  <div class="workoutZone__thumbSlot">
                    <el-image
                      v-if="row.ref_meta && row.ref_meta.thumbUrl"
                      class="workoutZone__thumb workoutZone__thumb--clickable"
                      :src="row.ref_meta.thumbUrl"
                      :preview-src-list="[row.ref_meta.thumbUrl]"
                      :z-index="4000"
                      fit="cover"
                      @click.native.stop
                    >
                      <div slot="error" class="workoutZone__thumbFallback">
                        <i class="el-icon-picture-outline" />
                      </div>
                    </el-image>
                    <div v-else class="workoutZone__thumb workoutZone__thumb--placeholder">
                      <i class="el-icon-picture-outline" aria-hidden="true" />
                    </div>
                  </div>
                  <div class="workoutZone__nameBlock">
                    <div class="workoutZone__name" :title="row.resource_id">
                      {{ row.resource_name || row.resource_id }}
                    </div>
                    <dl
                      v-if="refMetaLines(row).length"
                      class="workoutZone__refMeta"
                      :class="{
                        'workoutZone__refMeta--actions': zone.id === 'actions',
                        'workoutZone__refMeta--music': zone.id === 'music',
                        'workoutZone__refMeta--voices': zone.id === 'voices',
                      }"
                    >
                      <div
                        v-for="line in refMetaLines(row)"
                        :key="line.key"
                        class="workoutZone__refMetaRow"
                        :class="{
                          'workoutZone__refMetaRow--short': line.labelTone === 'short',
                          'workoutZone__refMetaRow--long': line.labelTone === 'long',
                        }"
                      >
                        <dt class="workoutZone__refMetaKey link-font-mono">{{ line.key }}</dt>
                        <dd
                          class="workoutZone__refMetaVal"
                          :class="{ 'workoutZone__refMetaVal--warn': line.muted }"
                        >
                          {{ line.value }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
              <!-- 有标签才展示 chip 行；无标签时整块不出现 -->
              <div
                v-if="zone.id === 'actions' && rowTagTitles(row).length"
                class="workoutZone__tagsBlock"
              >
                <div class="workoutZone__tagChips">
                  <span
                    v-for="(title, tagIndex) in rowTagTitles(row)"
                    :key="`${row.resource_id || index}-tag-${tagIndex}`"
                    class="workoutZone__tagChip"
                  >{{ title }}</span>
                </div>
              </div>

              <workout-action-binding-fields
                v-if="zone.id === 'actions'"
                :row="row"
                :field-labels="fieldLabels"
                :is-last="index === rows.length - 1"
                :action-index="index"
                :action-count="rows.length"
                @change="updateRowAt(index, $event)"
              />
              <div v-else-if="zone.id === 'music'" class="workoutZone__fields workoutZone__fields--music">
                <div class="workoutZone__loopBlock">
                  <span class="workoutZone__fieldLabel">
                    {{ fieldLabels.loop }} <span class="workoutZone__req">*</span>
                  </span>
                  <workout-bool-choice
                    :value="row.loop"
                    false-label="false"
                    true-label="true"
                    :disabled="musicLoopDisabled(row)"
                    :aria-label="fieldLabels.loop"
                    @input="updateRowField(index, 'loop', $event)"
                  />
                </div>
              </div>
            </div>
            <div class="workoutZone__actions">
              <el-button
                v-if="zone.id === 'actions'"
                type="text"
                size="small"
                class="workoutZone__copy"
                @click="copyRow(index)"
              >
                复制
              </el-button>
              <el-button type="text" size="small" class="workoutZone__remove" @click="removeRow(index)">
                移除
              </el-button>
            </div>
          </li>
        </ul>
        <div class="workoutZone__footer">
          <button
            type="button"
            class="workoutZone__pickBtn workoutZone__pickBtn--compact"
            :disabled="atCapacity"
            @click="$emit('pick')"
          >
            <i class="el-icon-plus workoutZone__pickIcon" aria-hidden="true" />
            <span class="workoutZone__pickLabel">{{ zone.continueLabel }}</span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import WorkoutActionBindingFields from './WorkoutActionBindingFields.vue'
import WorkoutActionBindingLegend from './WorkoutActionBindingLegend.vue'
import WorkoutBoolChoice from './WorkoutBoolChoice.vue'
import { WORKOUT_BINDING_FIELD_TIPS } from './workoutFormConstants'
import {
  duplicateActionBindingRow,
  formatTotalDurationLabel,
  normalizeActionBindingsList,
  normalizeMusicLoopForRow,
  readMusicLoopableFromBindingRow,
  resolveActionZoneTotalSeconds,
} from './workoutActionBindingCalculator'
import { formatBindingRefMetaLines } from './workoutBindingUtils'
import './workoutCollectionBinding.css'

export default {
  name: 'WorkoutCollectionBindingZone',
  components: { WorkoutActionBindingFields, WorkoutActionBindingLegend, WorkoutBoolChoice },
  props: {
    zone: { type: Object, required: true },
    rows: { type: Array, default: () => [] },
    maxBindings: { type: Number, required: true },
    fieldLabels: { type: Object, required: true },
  },
  data() {
    return {
      sortable: null,
      dragOriginalNextSibling: null,
      bindingTips: WORKOUT_BINDING_FIELD_TIPS,
    }
  },
  computed: {
    atCapacity() {
      return this.rows.length >= this.maxBindings
    },
    totalDurationLabel() {
      if (this.zone.id !== 'actions') {
        return ''
      }
      const rows = this.rows || []
      return formatTotalDurationLabel(resolveActionZoneTotalSeconds(rows))
    },
  },
  watch: {
    'rows.length'() {
      this.$nextTick(() => this.initSortable())
    },
  },
  mounted() {
    this.$nextTick(() => this.initSortable())
  },
  beforeDestroy() {
    this.destroySortable()
  },
  methods: {
    refMetaLines(row) {
      return formatBindingRefMetaLines(row.ref_meta, this.zone.id)
    },
    rowTagTitles(row) {
      const meta = row && row.ref_meta
      if (!meta || typeof meta !== 'object') {
        return []
      }
      if (Array.isArray(meta.tagTitles) && meta.tagTitles.length) {
        return meta.tagTitles.filter(Boolean)
      }
      return []
    },
    emitRows(nextRows) {
      const payload =
        this.zone.id === 'actions' ? normalizeActionBindingsList(nextRows) : nextRows
      this.$emit('update:rows', payload)
    },
    updateRowAt(index, nextRow) {
      const nextRows = this.rows.map((row, rowIndex) => (rowIndex === index ? nextRow : row))
      this.emitRows(nextRows)
    },
    updateRowField(index, field, value) {
      let nextRow = { ...this.rows[index], [field]: value }
      if (this.zone.id === 'music') {
        nextRow = normalizeMusicLoopForRow(nextRow)
      }
      this.updateRowAt(index, nextRow)
    },
    musicLoopDisabled(row) {
      return readMusicLoopableFromBindingRow(row) === false
    },
    copyRow(index) {
      if (this.atCapacity) {
        this.$message.warning(`本区最多绑定 ${this.maxBindings} 条`)
        return
      }
      const duplicated = duplicateActionBindingRow(this.rows[index])
      const nextRows = [...this.rows]
      nextRows.splice(index + 1, 0, duplicated)
      this.emitRows(nextRows)
    },
    removeRow(index) {
      const nextRows = [...this.rows]
      nextRows.splice(index, 1)
      this.emitRows(nextRows)
    },
    /**
     * 把当前条挪到运营输入的 1-based 位置；非法数字提示并还原显示。
     * 与拖动共用 splice 重排，落库顺序仍由数组下标决定。
     */
    commitOrderInput(fromIndex, event) {
      const input = event && event.target
      const raw = input ? String(input.value || '').trim() : ''
      const max = this.rows.length
      const parsed = Number.parseInt(raw, 10)
      const restoreDisplay = () => {
        if (input) {
          input.value = String(fromIndex + 1)
        }
      }
      if (!Number.isFinite(parsed) || parsed < 1 || parsed > max) {
        restoreDisplay()
        this.$message.warning(`请输入 1～${max} 的整数`)
        return
      }
      const toIndex = parsed - 1
      if (toIndex === fromIndex) {
        restoreDisplay()
        return
      }
      const nextRows = [...this.rows]
      const [moved] = nextRows.splice(fromIndex, 1)
      nextRows.splice(toIndex, 0, moved)
      this.emitRows(nextRows)
    },
    initSortable() {
      const el = this.$refs.sortableList
      if (!el) {
        this.destroySortable()
        return
      }
      this.destroySortable()
      this.sortable = Sortable.create(el, {
        // 整行 nameRow 可拖：右侧空白荒地也能拖；info / 序号输入不触发拖拽
        handle: '.workoutZone__dragHandle',
        filter: '.workoutZone__infoBlock, .workoutZone__order',
        preventOnFilter: false,
        animation: 150,
        onStart: (evt) => {
          // 记录拖拽前位置，onEnd 时还原 DOM 再交给 Vue patch
          this.dragOriginalNextSibling = evt.item.nextSibling
        },
        onEnd: (evt) => {
          const parent = evt.from
          const item = evt.item
          const originalNext = this.dragOriginalNextSibling
          this.dragOriginalNextSibling = null
          if (originalNext && originalNext.parentNode === parent) {
            parent.insertBefore(item, originalNext)
          } else {
            parent.appendChild(item)
          }

          const nextRows = [...this.rows]
          const [moved] = nextRows.splice(evt.oldIndex, 1)
          nextRows.splice(evt.newIndex, 0, moved)
          this.emitRows(nextRows)
        },
      })
    },
    destroySortable() {
      if (this.sortable) {
        this.sortable.destroy()
        this.sortable = null
      }
    },
    reinitSortable() {
      this.$nextTick(() => this.initSortable())
    },
  },
}
</script>

<style scoped>
.workoutZone__order {
  box-sizing: border-box;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  min-width: 28px;
  height: 28px;
  margin-right: 4px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--link-surface-muted, #f7f4ef);
  border: 1px solid var(--link-line-strong, #e5dfd6);
  font-size: 12px;
  font-weight: var(--link-font-semibold);
  color: var(--link-ink-secondary);
  text-align: center;
  outline: none;
}

.workoutZone__order:focus {
  border-color: var(--link-accent, #a67c52);
  background: #fff;
}

.workoutZone__actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.workoutZone__copy {
  padding: 0;
  color: var(--link-accent, #a67c52);
}

/* 音乐区：合集内循环 label 与 false/true 同一行（bool 组件默认 width:100% 会挤换行） */
.workoutZone__loopBlock {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  width: 100%;
}

.workoutZone__loopBlock .workoutZone__fieldLabel {
  margin: 0;
  white-space: nowrap;
  flex: 0 0 auto;
}

.workoutZone__loopBlock >>> .workoutBoolChoice {
  width: auto;
  min-width: 120px;
  max-width: 148px;
  flex: 0 0 auto;
}
</style>
